/**
 * ReferralCard — the referral card on the shared pipeline: the FOURTH card
 * type alongside the file FolderCard (manila folder), PrepSheetCard (cream
 * notebook) and CalendarCard (desk calendar leaf).
 *
 * Face v2 — "Version A" of the approved "Business card alone – three
 * versions" artboard: the cream business card IS the whole card. There is
 * no backing sheet and no rotation; it sits square and left-aligned like the
 * other three cards, at the same column width and with the same shadow, and
 * carries everything itself, top to bottom:
 *   - a paper clip, top centre, protruding above the card edge (inside the
 *     shared CARD_TOP_CLEARANCE, so it never collides with the column header
 *     or the card above);
 *   - the top row: prospect name (bold, dark) with "referred by ⟨referrer⟩ ·
 *     DD/MM" beneath in small grey, and the channel icon in a plum circle at
 *     top right;
 *   - the Re line in the handwritten face, plum;
 *   - a thin rail along the bottom above a hairline: three progress dots
 *     (Contacted / Meeting / Outcome) with a short status word, and the
 *     next-action date pill on the right.
 *
 * States (CLAUDE.md §36.3):
 *   - next action overdue → red date pill ("DD/MM · Nd late") and a rotated
 *     red FOLLOW UP stamp;
 *   - paused → amber ON HOLD stamp with the pause reason as its small line;
 *     the pill carries the resume date and turns red once that date arrives;
 *   - completed → navy stamp with the outcome, the status word reads the
 *     outcome ("Converted · file linked"), the rail's right side reads
 *     "Closed DD/MM", and while feedback_given_at is null an orange
 *     "FEEDBACK OWED · referrer" tag;
 *   - test record → TEST pill + dashed coral outline, as on the other cards.
 *
 * GEOMETRY: imports the SAME CARD_TOP_CLEARANCE FolderCard exports and the
 * other two cards consume, so all four card types start at the same y in a
 * column, and uses the same minHeight and shadow as PrepSheetCard /
 * CalendarCard. FolderCard / PrepSheetCard / CalendarCard are never
 * modified to accommodate this card.
 *
 * COLOURS: every colour is a CSS variable from variables.css (the
 * --cp-referral-* tokens plus the existing --cp-danger red and --cp-text
 * navy for the stamps). No hex literals here.
 *
 * DISPLAY ONLY: no transitions, no buttons. Visibility: every role sees
 * referral cards (Inbox aside, §36.7); only the Adviser gets the openable
 * affordance (`interactive`, the same app-level gate the other cards use,
 * §30). The card joins no file / prep / review statistic, SLA or
 * concurrency computation. It shows NO contact details — none are stored
 * (POPIA minimality, §36.5).
 */

import React from 'react';
import { Phone, MessageCircle, Mail, User } from 'lucide-react';
import { CARD_TOP_CLEARANCE } from './FolderCard';
import {
  REFERRAL_OUTCOME_LABELS,
  isReferralOverdue,
  isReferralResumeDue,
  isReferralFeedbackOwed,
  referralDaysOverdue,
} from '../../utils/referrals';

// Channel → icon (the plum circle at top right). Same four pictograms as the
// approved mockup: phone, message bubble, envelope, person.
const CHANNEL_ICONS = {
  phone: Phone,
  whatsapp_sms: MessageCircle,
  email: Mail,
  in_person: User,
};

/** 'DD/MM' from a 'YYYY-MM-DD' date or an ISO timestamp, for the compact rail. */
function shortDayMonth(value) {
  if (typeof value !== 'string' || value.length < 10) return '';
  return `${value.slice(8, 10)}/${value.slice(5, 7)}`;
}

export default function ReferralCard({ referral, onClick, interactive = true }) {
  const status = referral.pipelineStatus;
  const isPaused = status === 'paused';
  const isCompleted = status === 'completed';
  const overdue = !isPaused && isReferralOverdue(referral);
  const resumeDue = isPaused && isReferralResumeDue(referral);
  const daysLate = referralDaysOverdue(referral);
  const feedbackOwed = isReferralFeedbackOwed(referral);
  const outcomeLabel = referral.outcome ? REFERRAL_OUTCOME_LABELS[referral.outcome] : null;
  const referrerName = referral.referrer?.displayName || '';
  const ChannelIcon = CHANNEL_ICONS[referral.channel] || User;

  // Progress dots: Contacted → Meeting → Outcome. A dot fills once its
  // timestamp exists; the Outcome dot fills only on completion.
  const contacted = !!referral.progressContactedAt;
  const meeting = !!referral.progressMeetingAt;
  const dots = [contacted, meeting, isCompleted];

  // The short status word after the dots.
  let statusWord;
  if (isCompleted && outcomeLabel) {
    statusWord = referral.linkedFileId ? `${outcomeLabel} · file linked` : outcomeLabel;
  } else if (meeting) {
    statusWord = 'Meeting booked';
  } else if (contacted) {
    statusWord = 'Contacted';
  } else {
    statusWord = 'Not yet contacted';
  }

  // Right side of the rail: the next-action date pill (plum; red with
  // "· Nd late" when overdue), the resume date while paused, or the closing
  // date in plain grey once completed.
  let pill = null;
  let pillLate = false;
  let closed = null;
  if (isCompleted) {
    closed = referral.completedAt ? `Closed ${shortDayMonth(referral.completedAt)}` : 'Closed';
  } else {
    const late = isPaused ? resumeDue : overdue;
    const date = shortDayMonth(referral.nextActionOn);
    pill = late && daysLate > 0 ? `${date} · ${daysLate}d late` : date;
    pillLate = late;
  }

  // Same app-level role gate as the other three cards (§30): a non-permitted
  // role sees the card in full, without button semantics or the pointer.
  const gateBind = interactive
    ? {
        role: 'button',
        tabIndex: 0,
        'aria-label': `Open referral card for ${referral.prospectName}`,
        onKeyDown: (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick && onClick();
          }
        },
      }
    : {};

  return (
    <div
      onClick={interactive ? onClick : undefined}
      {...gateBind}
      // Return-to-dashboard lookup (CLAUDE.md §39).
      data-card-type="referral"
      data-card-id={referral.id}
      style={{
        ...styles.card,
        cursor: interactive ? 'pointer' : 'default',
        // Test records (CLAUDE.md §33): dashed coral outline, the same
        // existing --cp-warning token the other cards use.
        ...(referral.isTest ? { outline: '2px dashed var(--cp-warning)', outlineOffset: '0px' } : {}),
      }}
    >
      {/* Paper clip, top centre, protruding above the card edge */}
      <svg style={styles.clip} viewBox="0 0 16 36" fill="none" stroke="var(--cp-referral-clip)" strokeWidth="2" aria-hidden="true">
        <path d="M4 30V7a3 3 0 0 1 6 0v22a5 5 0 0 1-10 0V9" />
      </svg>

      {/* Top row: name + referrer on the left, channel circle on the right */}
      <div style={styles.top}>
        <div style={styles.topText}>
          <div style={styles.name}>
            {referral.prospectName}
            {referral.isTest && <span style={styles.testPill}>TEST</span>}
          </div>
          <div style={styles.ref}>
            referred by <b style={styles.refName}>{referrerName}</b>
            {referral.receivedOn && ` · ${shortDayMonth(referral.receivedOn)}`}
          </div>
        </div>
        <span style={styles.channel} title={referral.channel || ''}>
          <ChannelIcon size={12} color="var(--cp-surface)" strokeWidth={2.2} />
        </span>
      </div>

      {/* The Re line, handwritten */}
      {referral.reLine && <div style={styles.re}>{referral.reLine}</div>}

      {/* Bottom rail: dots + status word | date pill */}
      <div style={styles.rail}>
        <span style={styles.dots}>
          {dots.map((on, i) => (
            <span key={i} style={{ ...styles.dot, ...(on ? styles.dotOn : {}) }} />
          ))}
          <span style={styles.statusWord}>{statusWord}</span>
        </span>
        {pill !== null && (
          <span style={{ ...styles.pill, ...(pillLate ? styles.pillLate : {}) }}>{pill}</span>
        )}
        {closed && <span style={styles.closed}>{closed}</span>}
      </div>

      {/* Stamps — rotated, ink-on-paper */}
      {overdue && !isCompleted && (
        <span style={{ ...styles.stamp, ...styles.stampRed }}>Follow up</span>
      )}
      {isPaused && (
        <span style={{ ...styles.stamp, ...styles.stampAmber }}>
          On hold
          {referral.pauseReason && <small style={styles.stampSmall}>{referral.pauseReason}</small>}
        </span>
      )}
      {isCompleted && outcomeLabel && (
        <span style={{ ...styles.stamp, ...styles.stampNavy }}>{outcomeLabel}</span>
      )}

      {/* Feedback-owed tag — completed, outcome set, referrer not yet told */}
      {feedbackOwed && (
        <span style={styles.feedbackTag}>FEEDBACK OWED · {referrerName}</span>
      )}
    </div>
  );
}

const styles = {
  card: {
    position: 'relative',
    // Same top clearance the other three cards reserve (imported, never
    // re-typed — see CARD_TOP_CLEARANCE in FolderCard.jsx). The clip's 8px
    // protrusion sits inside it.
    marginTop: CARD_TOP_CLEARANCE,
    boxSizing: 'border-box',
    minHeight: '120px',
    background: 'var(--cp-referral-card)',
    borderRadius: '5px',
    // The sibling cards' shadow, plus the business card's inset edge line.
    boxShadow:
      '0 2px 6px rgba(0,0,0,0.08), 0 8px 18px rgba(0,0,0,0.06), 0 0 0 1px var(--cp-referral-card-edge) inset',
    padding: '16px 14px 10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    fontFamily: "'Montserrat', 'DM Sans', sans-serif",
    color: 'var(--cp-text)',
  },
  clip: {
    position: 'absolute',
    left: '50%',
    top: '-8px',
    marginLeft: '-8px',
    width: '16px',
    height: '36px',
    pointerEvents: 'none',
  },
  top: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '8px',
  },
  topText: {
    minWidth: 0,
    flex: 1,
  },
  name: {
    fontSize: '15px',
    fontWeight: 700,
    color: 'var(--cp-text)',
    letterSpacing: '0.01em',
    lineHeight: 1.15,
    overflowWrap: 'break-word',
  },
  testPill: {
    display: 'inline-block',
    verticalAlign: 'middle',
    marginLeft: '6px',
    padding: '1px 6px',
    borderRadius: '8px',
    background: 'var(--cp-surface-alt)',
    border: '1px dashed var(--cp-warning)',
    color: 'var(--cp-warning)',
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    lineHeight: 1.3,
  },
  ref: {
    fontSize: '9px',
    color: 'var(--cp-text-muted)',
    fontWeight: 500,
    marginTop: '2px',
  },
  refName: {
    color: 'var(--cp-referral-ink)',
    fontWeight: 700,
  },
  channel: {
    flex: '0 0 22px',
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    background: 'var(--cp-referral-ink)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  re: {
    fontFamily: 'var(--font-handwritten)',
    fontSize: '17px',
    lineHeight: '18px',
    color: 'var(--cp-referral-ink)',
    fontWeight: 700,
    overflowWrap: 'break-word',
  },
  rail: {
    // Pushed to the bottom; the card's flex gap keeps it clear of the Re line.
    marginTop: 'auto',
    paddingTop: '8px',
    borderTop: '1px solid var(--cp-referral-hairline)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  dots: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    minWidth: 0,
  },
  dot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    border: '2px solid var(--cp-referral-ink)',
    boxSizing: 'border-box',
    flex: '0 0 10px',
  },
  dotOn: {
    background: 'var(--cp-referral-ink)',
  },
  statusWord: {
    fontSize: '7.5px',
    fontWeight: 600,
    letterSpacing: '0.06em',
    color: 'var(--cp-referral-label)',
    textTransform: 'uppercase',
    marginLeft: '2px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  pill: {
    fontSize: '8.5px',
    fontWeight: 700,
    color: 'var(--cp-surface)',
    background: 'var(--cp-referral-ink)',
    padding: '2px 7px',
    borderRadius: '3px',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  pillLate: {
    background: 'var(--cp-danger)',
  },
  closed: {
    fontSize: '8px',
    fontWeight: 600,
    color: 'var(--cp-text-muted)',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  stamp: {
    // Anchored just above the rail so the stamp lands on the handwriting,
    // never on the name and referrer line (the column is narrower than the
    // artboard's card, so the name usually wraps).
    position: 'absolute',
    right: '12px',
    bottom: '40px',
    fontWeight: 700,
    letterSpacing: '0.14em',
    fontSize: '12px',
    lineHeight: 1.1,
    padding: '3px 7px',
    border: '3px solid',
    borderRadius: '4px',
    transform: 'rotate(-12deg)',
    opacity: 0.85,
    mixBlendMode: 'multiply',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
  },
  stampRed: {
    color: 'var(--cp-danger)',
    borderColor: 'var(--cp-danger)',
  },
  stampAmber: {
    color: 'var(--cp-referral-amber)',
    borderColor: 'var(--cp-referral-amber)',
  },
  stampNavy: {
    color: 'var(--cp-text)',
    borderColor: 'var(--cp-text)',
  },
  stampSmall: {
    display: 'block',
    fontSize: '7px',
    letterSpacing: '0.08em',
    fontWeight: 600,
    textTransform: 'none',
    maxWidth: '120px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  feedbackTag: {
    position: 'absolute',
    right: '-1px',
    bottom: '30px',
    fontSize: '7.5px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    padding: '3px 8px 3px 10px',
    background: 'var(--cp-referral-orange)',
    color: 'var(--cp-surface)',
    clipPath: 'polygon(8px 0, 100% 0, 100% 100%, 8px 100%, 0 50%)',
    whiteSpace: 'nowrap',
    maxWidth: '85%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    pointerEvents: 'none',
  },
};
